import React, { useEffect, useState, useMemo } from 'react';
import { MRT_ColumnDef, useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { Typography } from '@mui/material';
import { useFetch } from '../../utils/FetchTemplate';
import { useMembers } from '../../contexts/MemberContext';
import { useSetting } from '../../contexts/SettingContext';

interface IPaymentHistoryItem {
  paymentId: string;
  serviceId: string;
  memberId: string;
  discountType?: 'fixed' | 'percent' | 'none';
  discountAmount?: number;
  pricePerHour?: number;
  hoursPurchased?: number;
  hoursRemaining?: number;
  createdAt: string;
  memberName?: string;
  serviceName?: string;
}

const PaymentHistory: React.FC = () => {
  const [payments, setPayments] = useState<IPaymentHistoryItem[]>([]);
  const [rawPayments, setRawPayments] = useState<IPaymentHistoryItem[]>([]);
  const { postData } = useFetch();
  const { members } = useMembers();
  const { settings } = useSetting();

  const calculateTotalAmount = (payment: IPaymentHistoryItem): number => {
    // For VIP services: use pricePerHour * hoursPurchased
    if (payment.pricePerHour && payment.hoursPurchased) {
      const originalAmount = payment.pricePerHour * payment.hoursPurchased;
      
      if (!payment.discountAmount || payment.discountType === 'none') {
        return originalAmount;
      }
      
      let discount = 0;
      if (payment.discountType === 'fixed') {
        discount = payment.discountAmount;
      } else if (payment.discountType === 'percent') {
        discount = (originalAmount * payment.discountAmount) / 100;
      }
      
      return Math.max(0, originalAmount - discount);
    }
    
    // For group services: fall back to service price
    const service = settings.services?.find(s => s.serviceId === payment.serviceId);
    if (!service?.price) {
      return 0;
    }
    
    const originalAmount = service.price;
    
    if (!payment.discountAmount || payment.discountType === 'none') {
      return originalAmount;
    }
    
    let discount = 0;
    if (payment.discountType === 'fixed') {
      discount = payment.discountAmount;
    } else if (payment.discountType === 'percent') {
      discount = (originalAmount * payment.discountAmount) / 100;
    }
    
    return Math.max(0, originalAmount - discount);
  };

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  useEffect(() => {
    // Enrich payments with member names and service names when data becomes available
    if (rawPayments.length > 0) {
      const enrichedPayments = rawPayments.map(payment => {
        const member = members.find(m => m.memberId === payment.memberId);
        const service = settings.services?.find(s => s.serviceId === payment.serviceId);
        
        return {
          ...payment,
          memberName: member ? `${member.firstName} ${member.lastName}` : payment.memberId,
          serviceName: service?.name || payment.serviceId
        };
      });

      setPayments(enrichedPayments);
    }
  }, [rawPayments, members, settings.services]);

  const fetchPaymentHistory = async () => {
    try {
      const paymentsData = await postData('/api/payment/listAllByAdmin', {});
      setRawPayments(paymentsData as IPaymentHistoryItem[] || []);
    } catch (error) {
      console.error('Failed to fetch payment history:', error);
    }
  };

  const paymentColumns: Array<MRT_ColumnDef<IPaymentHistoryItem>> = useMemo(() => ([
    { 
      accessorKey: 'memberName', 
      header: 'Member',
      size: 120
    },
    { 
      accessorKey: 'serviceName', 
      header: 'Service',
      size: 120
    },
    { 
      accessorKey: 'discountType', 
      header: 'Discount',
      size: 80,
      Cell: ({ cell }) => {
        const value = cell.getValue<string>();
        return value === 'none' ? '-' : value;
      }
    },
    { 
      accessorKey: 'discountAmount', 
      header: 'Disc. Amt',
      size: 80,
      Cell: ({ cell }) => {
        const value = cell.getValue<number>();
        return value ? `$${value.toFixed(0)}` : '-';
      }
    },
    { 
      accessorKey: 'pricePerHour', 
      header: '$/Hr',
      size: 60,
      Cell: ({ cell }) => {
        const value = cell.getValue<number>();
        return value ? `$${value.toFixed(0)}` : '-';
      }
    },
    { 
      accessorKey: 'hoursPurchased', 
      header: 'Hrs',
      size: 50,
      Cell: ({ cell }) => {
        const value = cell.getValue<number>();
        return value || '-';
      }
    },
    { 
      accessorKey: 'hoursRemaining', 
      header: 'Remaining',
      size: 70,
      Cell: ({ cell }) => {
        const value = cell.getValue<number>();
        return value || '-';
      }
    },
    { 
      accessorKey: 'totalAmount', 
      header: 'Total Paid',
      size: 80,
      Cell: ({ row }) => {
        const totalAmount = calculateTotalAmount(row.original);
        return totalAmount > 0 ? `$${totalAmount.toFixed(0)}` : '-';
      }
    },
    { 
      accessorKey: 'createdAt', 
      header: 'Date',
      size: 100,
      Cell: ({ cell }) => {
        const date = new Date(cell.getValue<string>());
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: '2-digit'
        });
      }
    },
  ]), []);

  const table = useMaterialReactTable({ 
    columns: paymentColumns, 
    data: payments, 
    initialState: { 
      pagination: { pageIndex: 0, pageSize: 15 },
      sorting: [{ id: 'createdAt', desc: true }],
      density: 'compact'
    },
    enableFullScreenToggle: false,
    enableDensityToggle: true,
    muiTableContainerProps: {
      sx: { maxHeight: '600px' }
    },
    muiTableBodyCellProps: {
      sx: { 
        fontSize: '0.875rem',
        padding: '8px 12px'
      }
    },
    muiTableHeadCellProps: {
      sx: { 
        fontSize: '0.875rem',
        fontWeight: 'bold',
        padding: '8px 12px'
      }
    }
  });

  const totalAmountPaid = useMemo(() => {
    return payments.reduce((sum, payment) => sum + calculateTotalAmount(payment), 0);
  }, [payments]);

  return (
    <div>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Payment History ({payments.length} payments) - Total: ${totalAmountPaid.toFixed(2)}
      </Typography>
      <MaterialReactTable table={table} />
    </div>
  );
};

export default PaymentHistory; 